import { memo } from "react";
import { ChevronDown } from "lucide-react";
import { useShallow } from "zustand/shallow";
import { useMapRouteStore } from "@/stores/mapRouteStore";

type Props = {
  onExpand: () => void;
};

// Вынесены за пределы компонента чтобы не пересоздаваться при каждом рендере
const formatDurationMinutes = (seconds: number) => {
  return Math.ceil(seconds / 60);
};

const formatDistanceValue = (meters: number) => {
  if (meters >= 1000) {
    return { value: (meters / 1000).toFixed(1), unit: "км" };
  }
  return { value: Math.round(meters), unit: "м" };
};

const CollapsedRouteCard = memo(function CollapsedRouteCard({ onExpand }: Props) {
  const { route, direction } = useMapRouteStore(
    useShallow((s) => ({
      route: s.route,
      direction: s.direction,
    })),
  );

  if (!direction) return null;

  const { totalDuration, totalDistance } = direction.segmentationRoute;
  const distance = formatDistanceValue(totalDistance);

  return (
    <div className="px-4 pt-1 pb-4">
      <div
        onClick={onExpand}
        className="rounded-[10px] p-[14px] cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lg"
        style={{
          background: "linear-gradient(135deg, #1a5fb4 0%, #0d3a7a 100%)",
          boxShadow: "0 8px 24px rgba(13, 58, 122, 0.25)",
        }}
      >
        {/* Top row */}
        <div className="flex items-center gap-[10px] mb-[10px]">
          {/* Mini markers */}
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-white" />
            <div
              className="w-4 h-0.5 rounded-sm"
              style={{
                background: "linear-gradient(90deg, white, #C4A962)",
              }}
            />
            <div className="w-2 h-2 rounded-full bg-[#C4A962]" />
          </div>

          {/* Route text */}
          <div className="flex-1 text-sm font-semibold text-white truncate">
            {route.start || "Начало"} → {route.end || "Конец"}
          </div>

          {/* Chevron */}
          <div className="w-[26px] h-[26px] bg-white/15 rounded-md flex items-center justify-center">
            <ChevronDown className="w-[14px] h-[14px] text-white" />
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-[14px]">
          <div className="flex items-baseline gap-[3px]">
            <span className="text-lg font-bold text-white">
              {formatDurationMinutes(totalDuration)}
            </span>
            <span className="text-xs text-white/70">мин</span>
          </div>
          <div className="flex items-baseline gap-[3px]">
            <span className="text-lg font-bold text-white">{distance.value}</span>
            <span className="text-xs text-white/70">{distance.unit}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

export default CollapsedRouteCard;
